import { motion } from 'framer-motion';

interface CaseFilterProps {
  selectedTypes: string[];
  onTypeChange: (types: string[]) => void;
}

const customerTypes = [
  {
    id: 'market',
    label: '批发市场商户',
    icon: 'ri-store-2-line',
    description: '多品类、小批量、无进项',
    highlight: true
  },
  {
    id: 'ecommerce',
    label: '跨境电商本土店',
    icon: 'ri-shopping-cart-line',
    description: '国内发货、海外销售'
  },
  {
    id: 'agent',
    label: '境外采购代理',
    icon: 'ri-global-line',
    description: '代采代发、利润薄'
  },
  {
    id: 'no-invoice',
    label: '无进项贸易公司',
    icon: 'ri-file-list-line',
    description: '上游复杂、发票缺失',
    highlight: true
  },
  {
    id: 'supply-chain',
    label: '外贸供应链',
    icon: 'ri-links-line',
    description: '被动配合、风险隔离'
  },
  {
    id: 'sensitive',
    label: '涉敏国家业务',
    icon: 'ri-shield-check-line',
    description: '合规要求高、可追溯'
  }
];

const CaseFilter = ({ selectedTypes, onTypeChange }: CaseFilterProps) => {
  const handleTypeClick = (typeId: string) => {
    if (selectedTypes.includes(typeId)) {
      onTypeChange(selectedTypes.filter(t => t !== typeId));
    } else {
      onTypeChange([...selectedTypes, typeId]);
    }
  };

  const handleClearAll = () => {
    onTypeChange([]);
  };

  return (
    <section className="bg-white border-b border-neutral-200 sticky top-20 z-40 shadow-sm">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-neutral-900 mb-1">
              选择您的业务类型
            </h2>
            <p className="text-sm text-neutral-600">
              快速找到与您业务相似的案例
            </p>
          </div>
          {selectedTypes.length > 0 && (
            <button
              onClick={handleClearAll}
              className="text-sm text-[#1A3A5C] hover:text-[#FFB800] transition-colors whitespace-nowrap"
            >
              清除筛选
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {customerTypes.map((type) => {
            const isSelected = selectedTypes.includes(type.id);
            const isHighlight = type.highlight && selectedTypes.length === 0;

            return (
              <motion.button
                key={type.id}
                onClick={() => handleTypeClick(type.id)}
                className={`relative p-5 rounded-xl border-2 text-left transition-all cursor-pointer ${
                  isSelected
                    ? 'border-[#FFB800] bg-[#FFB800]/5'
                    : isHighlight
                    ? 'border-[#1A3A5C]/30 bg-[#1A3A5C]/5'
                    : 'border-neutral-200 bg-white hover:border-[#1A3A5C]/20'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isHighlight && (
                  <div className="absolute -top-2 -right-2 bg-[#FFB800] text-white text-xs px-2 py-1 rounded-full whitespace-nowrap">
                    热门
                  </div>
                )}
                
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'bg-[#FFB800]' : 'bg-[#1A3A5C]'
                  }`}>
                    <i className={`${type.icon} text-2xl text-white`}></i>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className={`font-bold text-base ${
                        isSelected ? 'text-[#1A3A5C]' : 'text-neutral-900'
                      }`}>
                        {type.label}
                      </h3>
                      {isSelected && (
                        <i className="ri-checkbox-circle-fill text-[#FFB800] text-lg"></i>
                      )}
                    </div>
                    <p className="text-sm text-neutral-600">
                      {type.description}
                    </p>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>

        {selectedTypes.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 flex items-center gap-2 text-sm text-neutral-600"
          >
            <i className="ri-filter-3-line"></i>
            <span>已选择 {selectedTypes.length} 个类型</span>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default CaseFilter;